import { useState } from 'react';
import type { EventInput, LoggedEvent, MeResponse } from '../api';
import { messageFor } from '../App';

export /** Shared add/edit form. Duration is never entered — the server derives it from the times. */
function EventForm({
  subjects,
  kids,
  initial,
  onSave,
  onCancel,
}: {
  subjects: MeResponse['subjects'];
  kids: NonNullable<MeResponse['children']>;
  initial?: LoggedEvent;
  onSave: (input: Partial<EventInput>) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<EventInput>({
    date: initial?.date ?? new Date().toISOString().slice(0, 10),
    startTime: initial?.startTime ?? '09:00',
    endTime: initial?.endTime ?? '10:00',
    subject: initial?.subject ?? subjects?.[0]?.id ?? 'reading',
    description: initial?.description ?? '',
    childIds: initial?.childIds ?? (kids[0] ? [kids[0].childId] : []),
    location: initial?.location ?? 'home',
  });
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const minutes = durationMinutes(form.startTime, form.endTime);

  return (
    <div>
      {error && <div className="notice error">{error}</div>}
      <div className="row">
        <div>
          <label>Date</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div className="narrow">
          <label>Start</label>
          <input type="time" value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
        </div>
        <div className="narrow">
          <label>End</label>
          <input type="time" value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
        </div>
        <div className="narrow">
          <label>Duration</label>
          <input readOnly value={minutes === undefined ? '—' : `${(minutes / 60).toFixed(2)} hrs`} />
        </div>
      </div>
      <div className="row">
        <div>
          <label>Subject</label>
          <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}>
            {subjects?.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.label}{subject.core ? ' (core)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Where</label>
          <select
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value as 'home' | 'away' })}
          >
            <option value="home">At home</option>
            <option value="away">Away from home</option>
          </select>
        </div>
        <div>
          <label>Children</label>
          <select
            multiple
            value={form.childIds}
            onChange={(e) =>
              setForm({
                ...form,
                childIds: Array.from(e.target.selectedOptions, (option) => option.value),
              })
            }
          >
            {kids.map((child) => (
              <option key={child.childId} value={child.childId}>{child.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="field" style={{ marginTop: 12 }}>
        <label>What was covered</label>
        <textarea
          rows={2}
          value={form.description}
          placeholder="Identified vegetables in the garden and discussed what each plant needs to grow."
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <button
        className="primary"
        disabled={busy || minutes === undefined || !form.description.trim() || form.childIds.length === 0}
        onClick={async () => {
          setBusy(true);
          setError(undefined);
          try {
            await onSave(form);
          } catch (err) {
            setError(messageFor(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        Save
      </button>{' '}
      <button onClick={onCancel}>Cancel</button>
      {minutes === undefined && (
        <span className="muted" style={{ marginLeft: 10 }}>
          The end time has to be after the start time.
        </span>
      )}
    </div>
  );
}


function durationMinutes(start: string, end: string): number | undefined {
  const parse = (value: string): number | undefined => {
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
  };
  const a = parse(start);
  const b = parse(end);
  if (a === undefined || b === undefined || b <= a) return undefined;
  return b - a;
}
